<template>
  <div class="kpt-detail-content-page">
    <vue-headful :title="`${remote.name} - Configuration`" />
    <div class="kpt-detail-content-actions">
      <div class="buttons" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin') && !isConfirmingSync">
        <b-tooltip :active="isSubscriptionPlanSyncUnsupported" 
          type="is-warning"
          position="is-right"
          label="The current Subscription does not support Synchronize.">
          <b-button type="is-primary is-outlined" size="is-small" icon-left="cloud-upload-alt" :loading="syncing" :disabled="isSyncDisabled" @click="isConfirmingSync = true;">Synchronize</b-button>
        </b-tooltip>
        <b-button type="is-outlined" size="is-small" icon-left="sync" @click="refreshProviderState" :loading="isLoadingProviderState">Refresh</b-button>
      </div>
      <div v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin') && isConfirmingSync">
        <!-- confirm sync -->
        <confirm-remote-sync @success="startSync" @cancel="cancelSync"></confirm-remote-sync>
      </div>
    </div>
    <div v-if="config">
      <b-notification :closable="false" :active="preSyncErrors" type="is-info">
        <ul>
          <li v-for="(error, idx) in devicesMissingErrors" :key="idx">
            {{error}}
          </li>
        </ul>
      </b-notification>
      <hr v-if="preSyncErrors">
      <remote-provider-network-rich-state :show-config-link="false" :show-refresh="false" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')"/>
      <hr>
      <div class="kpt-configuration-grid" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
        <b-field label="Provisioned" class="is-unselectable" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
          <b-switch 
          :disabled="config.provisioned ? true : !canProvision"
          :title="config.provisioned && !!(activeOrFutureSubscription) ? 'You cannot deprovision a remote.': ''"
          :value="config.provisioned"
          @input="executeCommand(config.provisioned ? 'deprovision' : 'provision', config.provisioned)">
          {{config.provisioned ? 'Yes' : 'No'}}
          </b-switch>
        </b-field>
        <b-message type="is-light" class="content" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
          <p>To provision a remote, it must have an active or future Subscription. The following devices must also be defined:</p>
          <ul>
            <li>Antenna Model <i>(for satellite service and cell service)</i></li>
            <li>Modem Model &amp; Serial <i>(for satellite service)</i></li>
            <li>IP Addresses <i>(for satellite service)</i></li>
            <li>ICCID, IMEI, Router SN <i>(for cell service)</i></li>
          </ul>
          <p>Once a remote is provisioned, it cannot be deprovisioned from this UI. </p>
          <b-message type="is-warning">When synchronizing, satellite hardware and network configuration must be set for satellite provisioning and activation to take place on NMS. Cellular/hybrid hardware must be set for cellular provisioning and activation to take place on NMS.</b-message>
        </b-message>
        <b-field label="Activated" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
          <b-tooltip label="Sync in progress with NMS." :active="!!syncInProgress" position="is-right" type="is-warning">
            <b-switch :disabled="(!config.provisioned || !canActivate || !!syncInProgress)"
                      :value="!!activeOrFutureSubscription && config.activated" @input="executeCommand(config.activated ? 'deactivate' : 'activate', config.activated)">
              {{config.activated ? 'Yes' : 'No'}}
            </b-switch>
          </b-tooltip>
        </b-field>
        <b-message type="is-light" class="content" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
          To activate or deactivate a remote, it must first be provisioned and satisfy all of the provisioning hardware requirements.
        </b-message>
        <b-field label="AllowCellTraffic" v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
          <b-switch :value="config.enablecelltraffic" @input="executeCommand(config.enablecelltraffic ? 'disablecelltraffic':'enablecelltraffic', config.enablecelltraffic)">
            {{config.enablecelltraffic ? 'Yes' : 'No'}}
          </b-switch>
        </b-field>
        <b-message type="is-light" class="content">
          This flag controls whether an activated remote is allowed to pass cell traffic.
        </b-message>
      </div>
      <hr>
      <flipper ref="flipper" height="175px">
        <template v-slot:front="{ toggle }">
          <div class="kpt-configuration-grid" v-if="!privateModeConfirmVisible && $hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
            <b-field label="Private Mode">
              <b-button 
              :type="{ 'is-primary is-outlined': !config.private, 'is-danger': config.private }" 
              :disabled="(!remote.devices.modemSerial)"
              @click="toggle">
                <span v-if="!config.private">Enable</span>
                <span v-else>Disable</span>
              </b-button>
            </b-field>
            <div>
              <b-message type="is-light" class="content">
                <p>Setting or removing private mode requires modem model and serial to be defined.</p>
                <p>You must confirm the setting by signing your name.</p>
              </b-message>
            </div>
          </div>
        </template>
        <template v-slot:back="{ toggle }">
          <div v-if="$hasRole('Super User,Kymeta Admin,Product Support,Account Admin')">
            <div class="content" v-if="config.private">
              <p>You are about to disable Private Mode for this remote. When Private Mode is disabled, we will begin to collect location data (GPS, Satellite, and Beam data) for this remote.</p>
              <p>By typing your name below, you are authorizing this remote have its private mode setting disabled.</p>
            </div>
            <div class="content" v-else>
              <p>You are about to set this remote into Private Mode.  Private Mode prevents the collection of location data (GPS, Satellite, and Beam data) for this remote.  Note: This feature does not delete data already collected.</p>
              <p>By typing your name below, you are authorizing this remote to be set to private mode.</p>
            </div>
            <b-field grouped>
              <b-input v-model="privateModeSignature" placeholder="Type your name here" class="name-input" spellcheck="false" autocomplete="off" style="width: 250px"></b-input>
              <b-button type="is-primary" :disabled="!canSubmitPrivateMode" :loading="executingCommand" @click="executeCommand(config.private ? 'disablePrivateMode' : 'enablePrivateMode', config.private)">
                <span v-if="!config.private">Enable Private Mode</span>
                <span v-else>Disable Private Mode</span>
              </b-button>
              <b-button @click="toggle(); privateModeSignature = null;" style="margin-left: 12px">Cancel</b-button>  
            </b-field>
            <p>{{userName}}</p>
          </div>
        </template>
      </flipper>
    </div>
  </div>
</template>
<script>
import { orderBy } from 'lodash';

import RemoteProviderNetworkRichState from '../components/RemoteProviderNetworkRichStateComponent.vue';
import Flipper from '../../shared/FlipperComponent.vue';
import ConfirmRemoteSync from '../components/RemoteDetailConfirmSyncComponent.vue';

export default {
  components: {
    RemoteProviderNetworkRichState,
    Flipper,
    ConfirmRemoteSync
  },
  data() {
    return {
      helpActive: false,
      syncing: false,
      privateModeConfirmVisible: false,
      privateModeSignature: null,
      executingCommand: false,
      isConfirmingSync: false
    }
  },
  computed: {
    remote() { return this.$store.state.remotes.remote; },
    activeSubscription() { return this.$store.getters.activeSubscription; },
    futureSubscriptions() { return this.$store.getters.futureSubscriptions; },
    config() {
      return {
        private: this.remote.private,
        paused: this.remote.paused,
        provisioned: this.remote.provisioned,
        activated: this.remote.activated,
        enablecelltraffic: this.remote.enableCellTraffic
      }
    },
    isSubscriptionPlanSyncUnsupported() {
      return !this.activeOrFutureSubscription?.canSyncWithProviders;
      // allow hybrid & cell gear to process as normal (ie, custom subscription acts the same as normal sub for hybrid/cell gear)
      // return (!!this.activeOrFutureSubscription?.productShortcode?.includes('CUSTOM')) 
      //   ? !(this.activeOrFutureSubscription?.canSyncWithProviders && this.isCellHwAvailable)
      //   : !this.activeOrFutureSubscription?.canSyncWithProviders;
    },
    isSyncDisabled() {
      return (this.syncInProgress) 
        || (this.isLoadingProviderState) 
        || (!this.activeOrFutureSubscription) 
        || (this.syncing) 
        || (this.preSyncErrors)
        || (this.isSubscriptionPlanSyncUnsupported);
    },
    // We can't provision or activate if we're missing these properties
    preSyncErrors() {
      return this.devicesMissingErrors.length != 0;
    },
    syncInProgress() {
      return !!this.remote.syncInProgress;
    },
    devicesMissingErrors() {
      const errors = [];
      if (!this.activeOrFutureSubscription) errors.push('Remote does not have an active subscription or future subscription.');
      if (!this.remote.devices.antenna1Model) errors.push('Remote does not have an antenna model defined.');
      if (!this.remote.devices.modemSerial) errors.push('Remote does not have a modem serial defined.');
      if (!this.remote.devices.modemModel) errors.push('Remote does not have a modem model defined.');
      if (!this.remote.devices.bucModel) errors.push('Remote does not have a BUC model defined.');
      if (!this.hasValidNetworkConfig) errors.push('Remote does not have a valid network configuration (IP Addresses).');
      return errors;
    },
    userName() {
      return `${this.$store.state.user.user.firstName} ${this.$store.state.user.user.lastName}`;
    },
    canSubmitPrivateMode() {
      return (this.userName == this.privateModeSignature) && !this.executingCommand;
    },
    isLoadingProviderState() {
      return this.$store.state.remotes.isLoadingProviderState;
    },
    hasSatellite() { return !!this.activeOrFutureSubscription?.hasSatellite }, // if active subs have hasSatellite = true
    hasCellular() { return !!this.activeOrFutureSubscription?.hasCellular }, // if active subs have hasCellular = true, this also applies for Hybrid
    canProvision() {
      return (
        this.activeOrFutureSubscription
        && !!this.activeOrFutureSubscription?.canProvision
      )
    },
    canActivate() {
      return (
        this.activeSubscription
        && !!this.activeSubscription?.canActivate
      )
    },
    isCellHwAvailable() {
      return !!(this.remote?.devices?.cellRouterIccid)
        && !!(this.remote?.devices?.cellRouterImei)
        && !!(this.remote?.devices?.cellRouterSerial);
    },
    hasValidNetworkConfig() {
      const evolutionSubscription = this.activeOrFutureSubscription?.networkIdentifier == 'Evolution';
      if (!evolutionSubscription) return true;
      // if subscription have EVO / Lepton, check for Valid NetworkConfiguration > IP Addresses
      return this.remote?.networkConfiguration?.eth0IpAddress
        && this.remote?.networkConfiguration?.eth0SubnetMask
        && this.remote?.networkConfiguration?.sat0IpAddress
        && this.remote?.networkConfiguration?.sat0SubnetMask
        && this.remote?.networkConfiguration?.mgmtIpAddress
        && this.remote?.networkConfiguration?.mgmtSubnetMask
        && this.remote?.networkConfiguration?.dhcpGateway
        && this.remote?.networkConfiguration?.dhcpClientIpRangeStart
        && this.remote?.networkConfiguration?.dhcpClientIpRangeEnd
    },
    activeOrFutureSubscription() {
      if (this.activeSubscription) return this.activeSubscription;
      if (this.futureSubscriptions) return orderBy(this.futureSubscriptions, ['startDate'], ['asc'])[0];
      return null;
    }
  },
  methods: {
    async executeCommand(command, bool) {
      this.executingCommand = true;
      const result = await this.$store.dispatch('executeCommand', { id: this.remote.id, cmd: command });
      this.executingCommand = false;
      if (!result) {
        this.$buefy.toast.open({
          duration: 2500,
          message: `Error setting ${command} to ${bool}`,
          type: 'is-danger'
        });
        return;
      }
      if (this.privateModeConfirmVisible) this.privateModeConfirmVisible = false;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'executeRemotesCommand', 'Executed remotes command.', { remoteId: this.remote.id, cmd: command });
    },
    togglePrivateConfirm() {
      this.privateModeConfirmVisible = true;
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'toggleRemotesPrivate', 'Private mode, toggle clicked.', { remoteId: this.remote.id });
    },
    async refreshProviderState() {
      // KyAnalytics client plugin, event tracker
      this.$kya.event('remotes', 'refreshRemotesProviderState', 'Refresh network state clicked.', { remoteId: this.remote.id });
      await this.$store.dispatch('fetchProviderNetworkRichState', { id: this.remote.id });
    },
    startSync() {
      this.isConfirmingSync = false;
    },
    cancelSync() {
      this.isConfirmingSync = false;
    }
  }
}
</script>
<style lang="scss" scoped>
@import '../../styles/_mixins';

.kpt-configuration-grid {
  @include display-grid;
  grid-template-columns: 200px 1fr;
}
.kpt-detail-content-actions {
    .b-tooltip {
      margin-right: 8px;
    }
}

::v-deep .tooltip-content {
  margin-top: -4px;
}
</style>
